import { appSettings } from '@/appSettings';
import { useUser } from '@/login/composables/useUser';
import { defineStore, storeToRefs } from 'pinia';
import type { ComputedRef} from 'vue';
import { onMounted } from 'vue';

type UseAnalyticsReturn = {
    cookieBot: ComputedRef<CookieBot>;
};

type CookieBotStoreState = {
    _cookieBot: CookieBot;
};

type CookieBot = {
    statistics: boolean;
    marketing: boolean;
    necessary: boolean;
    preferences: boolean;
};

const useCookieBotStore = defineStore('cookiebot', {
    state: (): CookieBotStoreState => ({
        _cookieBot: {
            statistics: false,
            marketing: false,
            necessary: false,
            preferences: false
        },
    }),
    getters: {
        cookieBot: state => state._cookieBot
    },
    actions: {
        setCookieBot(cookieBot: CookieBot): void {
            this._cookieBot = cookieBot;
        }
    }
});

export function useCookiebot(overrideDev = false): UseAnalyticsReturn {
    const { isDev } = useUser();
    const { cookieBot } = storeToRefs(useCookieBotStore());

    function init(): void {
        if (isDev && overrideDev === false) {
            return;
        }

        const header = document.getElementsByTagName('head')[0];
        if (header == null) return;

        const cookiebot = header.querySelector('script[name="cookie-bot"]');

        if (cookiebot == null) {
            const script = document.createElement('script');
            script.setAttribute('name', 'cookie-bot');
            script.setAttribute('src', 'https://consent.cookiebot.com/uc.js');
            script.setAttribute('async', '');
            script.setAttribute('data-cbid', appSettings.cookiebotId);
            script.addEventListener('load', () => {
                (window as any).CookiebotCallback_OnAccept = (): void => {
                    if (window.Cookiebot == null) return;
                    useCookieBotStore().setCookieBot({
                        statistics: window.Cookiebot.consent.statistics,
                        marketing: window.Cookiebot.consent.marketing,
                        necessary: window.Cookiebot.consent.necessary,
                        preferences: window.Cookiebot.consent.preferences
                    });
                };
            });

            header.appendChild(script);
        }
    }

    onMounted(() => {
        if (window.Cookiebot == null) {
            init();
            return;
        }

        useCookieBotStore().setCookieBot({
            statistics: window.Cookiebot.consent.statistics,
            marketing: window.Cookiebot.consent.marketing,
            necessary: window.Cookiebot.consent.necessary,
            preferences: window.Cookiebot.consent.preferences
        });
    });

    return {
        cookieBot
    };
}
